import { useContext, useState } from 'react'
import { t } from 'ttag'
import { useMutation, useQuery } from 'react-query'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

import { api } from '@h2/api'
import { Globals } from '@h2/globals'
import { showUserFeedback } from '@h2/common/actions/showUserFeedback'
import { Loader } from '@h2/components/loading/loader'
import { Button } from '@h2/components/buttons/button'
import { Link } from '@h2/common/components/link'
import { Text } from '@h2/components/typography/text'
import { Container } from '@h2/components/layout/container'
import { PB90T, PB80T } from '@h2/components/colors'

import { Flex } from '../../components/layout/flex'
import { TrashIcon } from '../../components/icons'

const EarlyButton = styled(Link)`
  transition: all 0.2s ease-in-out;
  padding: 5px;
  margin: 5px;
  span {
    opacity: 0;
  }
`
const ExtraLargeText = styled(Text)`
  font-size: 26px;
`
const Table = styled.table``
const TableHeading = styled.th`
  border-bottom: 2px solid #ddd;
`
const TableRow = styled.tr`
    tr {
      padding: 15px 5px;
      line-height: 75px;
    } 
    &:hover {
      ${EarlyButton} {
          border: 1px solid ${PB80T};
          background-color: ${PB90T};
          width: 95%;
          text-align: left;
          img {
            transform: scale(1.1);
          }
          span {
            opacity: 1;
            font-size: 1.5rem;
          }
        }
      }
    }
`
const isCurrentYear = (game, currentProgramStartDate) => {
  const currentDate = new Date()
  let defaultStartYear = currentDate.getFullYear() - 1

  if (currentDate.getMonth() > 6) {
    defaultStartYear = currentDate.getFullYear()
  }

  //if not defined in the tenant, we'll use 6/1
  const defaultStartDate = new Date(defaultStartYear, 6, 1)
  const startDate = currentProgramStartDate
    ? new Date(currentProgramStartDate)
    : defaultStartDate

  return new Date(game.started) > startDate
}
const stripHash = str => {
  if (str.startsWith('#')) {
    return str.substring(1)
  }

  return str
}
const GameListing = () => {
  const dispatch = useDispatch()
  const [deletionId, setDeletionId] = useState(null)
  const { userProfile, currentProgramStartDate, isDemo } = useContext(Globals)
  const { data, isLoading, refetch } = useQuery(['game-listing', userProfile], () => {
    return api.fetch('/early/utl', { method: 'GET' })
  })
  const { mutate, isLoading: isMutating } = useMutation(
    () => api.fetch('/early/utl', { body: JSON.stringify({}), method: 'POST' }),
    {
      onSuccess: game => {
        window.location = `/early/game/${game.id}/home`
      },
      onError: () => {
        dispatch(showUserFeedback(t`Could not create new game`, 'error'))
      }
    }
  )
  const { mutate: deleteGame, isLoading: isDeleting } = useMutation(
    gameId => {
      setDeletionId(gameId)

      return api.fetch(`/early/utl/${gameId}`, { method: 'DELETE' })
    },
    {
      onSuccess: async () => {
        await refetch()
        setDeletionId(null)
      },
      onError: () => {
        dispatch(showUserFeedback(t`Could not create delete game`, 'error'))
        setDeletionId(null)
      }
    }
  )
  const dateIfOlder = game => {
    if (isCurrentYear(game, currentProgramStartDate)) {
      return ''
    }

    const startDate = new Date(game.started)
    const startYear = startDate.getFullYear()
    const startMonth = startDate.getMonth()
    const schoolStartDate = new Date(currentProgramStartDate)
    let str = `${startYear}-${startYear + 1 - 2000}`

    if (startMonth <= schoolStartDate.getMonth()) {
      str = `${startYear - 1}-${startYear - 2000}`
    }

    return ` (${str})`
  }
  const genTableRows = () => {
    const styleMap = { fontSize: '1.4rem', lineHeight: '75px' }

    return data.games.map((game, idx) => (
      <TableRow key={game.id} className={idx % 2 === 0 ? 'even' : 'odd'}>
        <td style={{ paddingLeft: '10px' }}>
          <EarlyButton
            href={`/early/game/${game.id}/home`}
            variant="button"
            ssr={true}
            display="outlined">
            <div style={{ display: 'inline-block', minWidth: '90px' }}>
              <img
                alt={t`Avatar used in game`}
                src={`/images/early/avatar/${game.face || '11'}/${stripHash(
                  game.skin || 'cccccc'
                )}/${stripHash(game.hair || 'cccccc')}/2f2f2f`}
                className="noscale avatar"
                style={{ height: '75px', verticalAlign: 'middle' }}
              />
            </div>
            <span>
              {t`Load ${game.name}'s Game`}
              {dateIfOlder(game)}
            </span>
          </EarlyButton>
        </td>
        <td style={styleMap}>{game.name}</td>
        <td style={styleMap}>{game.setup ? <i className="check"></i> : <span></span>}</td>
        <td style={styleMap} className="text-center">
          {game.setup ? (
            <span style={{ display: game.currentLevel > 2 ? 'inline-block' : 'none' }}>
              <a href={`/api/v1/early/presentation/${game.id}`}>
                <i className="fal fa-file-powerpoint" aria-hidden="true"></i>
              </a>
            </span>
          ) : (
            <span></span>
          )}
          {userProfile.isRoot || isDemo ? (
            <Button
              variant="link"
              icon={TrashIcon}
              isLoading={isDeleting && deletionId === game.id}
              onClick={() => deleteGame(game.id)}>
              <span className="sr-only">{t`Delete`}</span>
            </Button>
          ) : (
            <span></span>
          )}
        </td>
      </TableRow>
    ))
  }
  const mostRecentGameLink = () => {
    /*
     *  newEligible was updated to always be true, since we moved to adding lessons. For those who want a new game, they can
     *  and if not, they can select from the table. This replaced the condition where a new game could be started only
     *  once a year.
     *  Previous condition: !data.games?.find(game => isCurrentYear(game))
     */
    const newEligible = true
    const buttonsToReturn = []

    if (data.games && !newEligible && data.games.length > 0) {
      buttonsToReturn.push(
        <Link
          key="continueGameLink"
          ssr={true}
          href={`/early/game/${data.games[0].id}/home`}
          variant="button"
          display="default">
          {t`Continue Last Game`}
        </Link>
      )
    }

    if (!data.games?.length || userProfile.hasManager || newEligible) {
      buttonsToReturn.push(
        <Button
          key="newGameLink"
          onClick={mutate}
          display={data.games.length === 0 ? 'default' : 'blue'}
          isLoading={isMutating}>
          {t`Start a New Game`}
        </Button>
      )
    }

    return (
      <Flex gap="15px" justifyContent="center">
        {buttonsToReturn}
      </Flex>
    )
  }

  if (isLoading) {
    return <Loader />
  }

  return (
    <Container id="utl-list">
      <img
        src="/images/common/earlyexploration/other/interests.svg"
        alt={t`Cartoon of many people performing their jobs`}
      />
      <ExtraLargeText margin="30px 0">
        {t`Choose a previous game from the list below.`}
      </ExtraLargeText>

      <Table>
        <thead>
          <tr>
            <TableHeading></TableHeading>
            <TableHeading>{t`Name`}</TableHeading>
            <TableHeading>{t`Started`}</TableHeading>
            <TableHeading>{t`Actions`}</TableHeading>
          </tr>
        </thead>
        <tbody>{genTableRows()}</tbody>
        <tfoot>
          <tr>
            <td colSpan="4" className="text-center" style={{ padding: '15px' }}>
              {mostRecentGameLink()}
            </td>
          </tr>
        </tfoot>
      </Table>
      {userProfile?.primaryType !== 'student' && (
        <Flex justifyContent="right">
          <Link ssr={true} href="/files/pdfs/UTL_GetStarted.pdf" className="pull-right">
            {t`Teachers: Download our Quick Start Guide`}
          </Link>
        </Flex>
      )}
    </Container>
  )
}

export { GameListing }
